import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@material-ui/core/Dialog'
import CloseIcon from '@material-ui/icons/Close'
import numeral from 'numeral'
import moment from 'moment'

import styles from './styles.module.scss'

import { getCurrency } from '../../../helpers/currencies'
import { calculateMargin } from '../../../helpers/invoice'

const formatSummaryAmount = (value, currency) => {
  return `${numeral(value || 0).format('0.00')} ${getCurrency(currency).symbol}`
}

const convertPdfToImages = async (pdfUrl) => {
  const response = await fetch(pdfUrl)
  const buffer = await response.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  const pdf = await window.pdfjsLib.getDocument(bytes).promise
  const images = []

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber)
    const viewport = page.getViewport({ scale: 2 })
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (!context) {
      continue
    }

    canvas.height = viewport.height
    canvas.width = viewport.width

    await page.render({
      canvasContext: context,
      viewport,
    }).promise

    images.push(canvas.toDataURL('image/png'))
  }

  return images
}

const getAggregatedInfo = (invoice, lang) => {
  const {totalAmount, totalTax, totalWorkTimes, totalExpenses, totalManualEntries} = invoice.items.reduce((acc, item) => {
    return {
      ...acc,
      totalAmount: acc.totalAmount + numeral(item.total_price).value(),
      totalTax: acc.totalTax + numeral(item.vat || 0).value(),
      totalWorkTimes: acc.totalWorkTimes + (item.type === 'work_time' ? numeral(item.unit_price * item.quantity).value() : 0),
      totalExpenses: acc.totalExpenses + (item.type === 'expense' ? numeral(item.unit_price * item.quantity).value() : 0),
      totalManualEntries: acc.totalManualEntries + (item.type === 'manual_entry' ? numeral(item.unit_price * item.quantity).value() : 0),
    }
  }, {
    totalAmount: 0,
    totalTax: 0,
    totalWorkTimes: 0,
    totalExpenses: 0,
    totalManualEntries: 0,
  })
  const activity = {
    ...(invoice.created_by ? {created: `${moment(invoice.created_at).locale(lang).format('MMM DD, YYYY')} · ${invoice.created_by.name} ${invoice.created_by.surname}` } : {}),
    ...(invoice.edited_by ? {edited: `${moment(invoice.updated_at).locale(lang).format('MMM DD, YYYY')} · ${invoice.edited_by.name} ${invoice.edited_by.surname}` } : {}),
    ...(invoice.issued_by ? {issued: `${moment(invoice.issued_date).locale(lang).format('MMM DD, YYYY')} · ${invoice.issued_by.name} ${invoice.issued_by.surname}` } : {}),
    ...(invoice.marked_as_paid_by ? {marked_as_paid: `${moment(invoice.marked_as_paid_date).locale(lang).format('MMM DD, YYYY')} · ${invoice.marked_as_paid_by.name} ${invoice.marked_as_paid_by.surname}` } : {}),
  }
  const margin = calculateMargin(invoice.items, invoice.discount)
  const totalNet = totalWorkTimes + totalExpenses + totalManualEntries - invoice.discount
  const totalVat = totalNet * invoice.default_vat / 100

  return {
    invoiceDate: invoice.invoice_date ? moment(invoice.invoice_date).locale(lang).format('MMM DD YYYY') : '-',
    dueDate: invoice.due_date ? moment(invoice.due_date).locale(lang).format('MMM DD YYYY') : '-',
    period: moment(invoice.billing_period_start).locale(lang).format('MMM DD YYYY') + ' - ' + moment(invoice.billing_period_end).locale(lang).format('MMM DD YYYY'),
    // totalAmount: numeral(Math.max(0, totalAmount - invoice.discount)).format('0.00'),
    totalAmount: numeral(Math.max(0, totalNet + totalVat)).format('0.00'),
    totalNet: numeral(totalNet).format('0.00'),
    totalTax: numeral(totalVat).format('0.00'),
    totalWorkTimes: totalWorkTimes,
    totalExpenses: totalExpenses,
    totalManualEntries: totalManualEntries,
    margin: margin,
    activity: activity,
    invoice: invoice,
  }
}

const PreviewInvoice = forwardRef((props, ref) => {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [previewImages, setPreviewImages] = useState([])
  const [aggregatedInfo, setAggregatedInfo] = useState({invoice: {}, activity: {}})

  const ActivityTitleMap = {
    created: t('Invoice created'),
    edited: t('Invoice edited'),
    issued: t('Invoice issued'),
    marked_as_paid: t('Marked as paid'),
  }

  const summaryItems = [
    { label: t('Invoice date'), value: aggregatedInfo.invoiceDate || '-' },
    { label: t('Due date'), value: aggregatedInfo.dueDate || '-' },
    { label: t('Period'), value: aggregatedInfo.period || '-', style: {borderWidth: 2, borderColor: '#bdbdbd'} },
    { label: t('Work entries summary'), value: formatSummaryAmount(aggregatedInfo.totalWorkTimes, aggregatedInfo.invoice.currency) },
    { label: t('Expenses summary'), value: formatSummaryAmount(aggregatedInfo.totalExpenses, aggregatedInfo.invoice.currency), style: !numeral(aggregatedInfo.totalManualEntries).value() ? {borderWidth: 2, borderColor: '#bdbdbd'} : {} },
    ...(
      numeral(aggregatedInfo.totalManualEntries).value()
      ? [{ label: t('Manual lines summary'), value: formatSummaryAmount(aggregatedInfo.totalManualEntries, aggregatedInfo.invoice.currency), style: {borderWidth: 2, borderColor: '#bdbdbd'} }]
      : []
    ),
    ...(
      numeral(aggregatedInfo.invoice.discount).value()
      ? [{ label: t('Discount'), value: `- ${formatSummaryAmount(aggregatedInfo.invoice.discount, aggregatedInfo.invoice.currency)}` }]
      : []
    ),
    { label: t('Net Total'), value: formatSummaryAmount(aggregatedInfo.totalNet, aggregatedInfo.invoice.currency), style: {borderWidth: 2, borderColor: '#bdbdbd'} },
    { label: t('VAT / Tax'), value: formatSummaryAmount(aggregatedInfo.totalTax, aggregatedInfo.invoice.currency), style: {borderWidth: 2, borderColor: '#bdbdbd'} },
    { label: t('Total'), value: formatSummaryAmount(aggregatedInfo.totalAmount, aggregatedInfo.invoice.currency), style: {color: '#0085FF', fontSize: 17, fontWeight: '800'} },
    { label: t('Margin'), value: formatSummaryAmount(aggregatedInfo.margin), style: {color: '#32BF1F'}  }
  ]

  const close = useCallback(() => {
    setPreviewImages([])
    setOpen(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: async ({pdfUrl, invoice}) => {
      setOpen(true)

      const images = await convertPdfToImages(pdfUrl)
      setPreviewImages(images)
      const data = getAggregatedInfo(invoice, i18n.language)
      setAggregatedInfo(data)
    },
    close,
  }), [close])

  return (
    <Dialog
      onClose={close}
      open={open}
      maxWidth={'xl'}
      PaperProps={{ style: { maxWidth: 1080, width: '100%' } }}
    >
      <div className={styles.previewDialog}>
        <div className={styles.previewHeader}>
          <div className={styles.previewTitle}>{t('Invoice preview')}</div>
          <div className={styles.previewCloseButton} onClick={close}>
            <CloseIcon />
          </div>
        </div>
        <div className={styles.previewBody}>
          <div style={{flex: 1}}>
          {
            previewImages.length
              ? <div className={styles.previewPages}>
                  {
                    previewImages.map((previewImage, index) => (
                      <img
                        key={index}
                        src={previewImage}
                        alt={`${t('Invoice preview')} - ${index + 1}`}
                        className={styles.previewImage}
                      />
                    ))
                  }
                </div>
              : <div className={styles.previewLoading}>{t('Unable to generate preview')}</div>
          }
          </div>
          <div className={styles.invoiceDetails}>
            <div className={styles.aggregatedInfo}>
              <div className={styles.summaryTitle}>{t('Summary')}</div>
              <div className={styles.summaryRows}>
                {summaryItems.map(item => (
                  <div key={item.label} className={styles.summaryRow} style={item.style}>
                    <div className={styles.summaryLabel}>{item.label}</div>
                    <div className={styles.summaryValue} style={item.style}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
            {
              Object.keys(aggregatedInfo.activity).length
                ? <div className={styles.aggregatedInfo}>
                    <div className={styles.summaryTitle}>{t('Activity')}</div>
                    <div className={styles.summaryRows}>
                      {
                        Object.entries(aggregatedInfo.activity).map(([key, value]) => {
                          return (
                            <div key={key} className={styles.summaryRow}>
                              <div className={styles.summaryValue}>{ActivityTitleMap[key]}</div>
                              <div className={styles.summaryLabel}>{value}</div>
                            </div>
                          )
                        })
                      }
                    </div>
                  </div>
                : null
            }
          </div>
        </div>
      </div>
    </Dialog>
  )
})

export default PreviewInvoice
